import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { deepCopy } from '../../../../utils/variables';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { useErrorHandling } from '../../../hooks/use-error-handling';

interface PatchGcpParams {
  geodataId: string;
  gcpId: string;
  status: 'ENABLED' | 'DISABLED' | 'CHECKPOINT';
}

export const usePatchGcpMutation = () => {
  const { clearErrors: clearApiErrors, handleError, buildErrorList } = useErrorHandling();
  const queryClient = useQueryClient();
  const errorList = useMemo(() => buildErrorList(), [buildErrorList]);
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: async (params: PatchGcpParams) => {
      return SkyMapAxiosServiceFactory.instance.createGeodataServiceV0().patchGcp({
        path: {
          geodataId: params.geodataId,
          gcpId: params.gcpId,
        },
        body: {
          status: params.status,
        },
      });
    },
    retry: false,
    networkMode: 'always',
    onSuccess: (response, variables) => {
      // Update the geodata in React Query cache inline
      queryClient.setQueryData(
        ['fetchGeodataById', variables.geodataId],
        (oldData: GeodataModel | undefined) => {
          if (!oldData || !oldData.gcpCollection?.points) {
            return oldData;
          }

          const copy = deepCopy(oldData);
          const matchingGcp = copy.gcpCollection?.points.find(
            (point) => point.id === variables.gcpId,
          );
          if (matchingGcp) {
            matchingGcp.status = response.data.status;
            matchingGcp.enu = response.data.enu;
            matchingGcp.name = response.data.name;
          }

          return copy;
        },
      );

      // Show success message
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: t('aligner.gcpStatusUpdated', { ns: 'cloudProcessing', name: response.data.name }),
      });
    },
    onMutate: () => clearApiErrors(),
    onError: (err) => handleError(err),
  });

  return {
    patchGcp: mutation.mutate,
    patchGcpAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    errorList,
  };
};
